import { useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "../../auth/auth.css";
import { Modal } from "../../auth/Modal.tsx";
import { RevealInput } from "../../auth/RevealInput.tsx";
import { OtpInput } from "../../components/OtpInput.tsx";
import { useOptionalAuth } from "../../auth/AuthContext.tsx";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import {
  DISCOVERED_STATUSES,
  decisionMessage,
  newOpId,
  sendDecision,
  type DiscoveredAgent,
  type DiscoveredStatus,
  type RegisterAnswer,
} from "./discovered-client.ts";
import { DISCOVERED_QUERY_ROOT, useDiscovered } from "./use-discovered.ts";
import "./discovered.css";

/**
 * Discovered agents (G1 #137): the callers the Gateway MCP refused because they presented no usable
 * identity, one row per caller key. An administrator can REGISTER a caller (the AIS signs a single-use,
 * expiring enrollment invitation, shown once) or REJECT it (known and refused, hidden from the default
 * list). Both need a reason and a fresh two-factor code and are audited with who and why.
 *
 * The buttons only mirror what the server allows. The BFF, the AIS and core enforce the admin role, the
 * reason, the step-up and the tenant regardless of what is shown here. The list polls every 30 s.
 */

const TAB_LABEL: Readonly<Record<DiscoveredStatus, string>> = { new: "New", registered: "Registered", rejected: "Rejected" };
const EMPTY_CAUSE: Readonly<Record<DiscoveredStatus, string>> = {
  new: "No unidentified caller has been refused yet. A caller appears here within a minute of its first refused call.",
  registered: "No caller has been registered yet.",
  rejected: "No caller has been rejected yet.",
};
const ADMIN_ONLY = "Only a team administrator can register or reject a caller. The server enforces this regardless.";

function topTools(agent: DiscoveredAgent): string {
  if (agent.tools.length === 0) return "—";
  const shown = agent.tools.slice(0, 3).map((t) => t.tool);
  const more = agent.tools.length - shown.length;
  return more > 0 ? `${shown.join(", ")} +${String(more)}` : shown.join(", ");
}
function client(agent: DiscoveredAgent): string {
  const name = agent.client_name ?? "";
  const version = agent.client_version ?? "";
  if (name === "" && version === "") return "—";
  return version === "" ? name : `${name} ${version}`.trim();
}
function shortKey(key: string): string {
  return `${key.slice(0, 14)}…${key.slice(-6)}`;
}

type Dialog =
  | { readonly kind: "decide"; readonly action: "register" | "reject"; readonly agent: DiscoveredAgent }
  | { readonly kind: "invitation"; readonly agent: DiscoveredAgent; readonly answer: RegisterAnswer["invitation"] };

export function DiscoveredAgentsScreen(): ReactNode {
  const isAdmin = useOptionalAuth()?.identity?.role === "admin";
  const [status, setStatus] = useState<DiscoveredStatus>("new");
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const query = useDiscovered(status, cursor);
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const tabsId = useId();

  const selectStatus = (next: DiscoveredStatus): void => {
    setStatus(next);
    setCursors([null]);
    setNotice(null);
  };
  const page = query.data;
  const state: ScreenState = query.isError && page === undefined
    ? { kind: "error", message: "The discovered callers could not be loaded." }
    : page === undefined
      ? { kind: "loading" }
      : { kind: "ready" };

  const refresh = async (message: string): Promise<void> => {
    setNotice(message);
    await queryClient.invalidateQueries({ queryKey: DISCOVERED_QUERY_ROOT });
  };
  const open = (action: "register" | "reject", agent: DiscoveredAgent, trigger: HTMLElement): void => {
    triggerRef.current = trigger;
    setNotice(null);
    setDialog({ kind: "decide", action, agent });
  };

  return (
    <ScreenScaffold
      title="Discovered agents"
      subtitle="Callers the Gateway MCP refused because they presented no usable identity. Register one to issue a single-use enrollment invitation, or reject it."
      state={state}
    >
      <div className="disc">
        <div className="disc__bar">
          <div role="tablist" aria-label="Caller status" className="disc__tabs" id={tabsId}>
            {DISCOVERED_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={status === s}
                className="disc__tab"
                data-active={status === s ? "true" : "false"}
                onClick={() => selectStatus(s)}
              >
                {TAB_LABEL[s]}
              </button>
            ))}
          </div>
          <p className="disc__fresh">Updated within a minute of the refusal. Refreshes every 30 seconds.</p>
        </div>
        {!isAdmin && (
          <p className="disc__note" role="note">
            {ADMIN_ONLY}
          </p>
        )}
        {notice !== null && (
          <p className="disc__notice" role="status">
            {notice}
          </p>
        )}
        {page !== undefined && page.items.length === 0 ? (
          <div className="state-box" data-kind="empty">
            <strong>Nothing to show</strong>
            <span>{EMPTY_CAUSE[status]}</span>
          </div>
        ) : page !== undefined ? (
          <>
            <table className="disc__table" aria-label={`${TAB_LABEL[status]} callers`}>
              <thead>
                <tr>
                  <th scope="col">Caller</th>
                  <th scope="col">First seen</th>
                  <th scope="col">Last seen</th>
                  <th scope="col">Calls</th>
                  <th scope="col">Client</th>
                  <th scope="col">Transport</th>
                  <th scope="col">Gateways</th>
                  <th scope="col">Tools</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((agent) => (
                  <Row key={agent.caller_key} agent={agent} isAdmin={isAdmin} onAct={open} />
                ))}
              </tbody>
            </table>
            <div className="disc__pager">
              <button type="button" className="disc__btn" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
                Previous
              </button>
              <button
                type="button"
                className="disc__btn"
                disabled={page.next_cursor === null}
                onClick={() => setCursors((c) => (page.next_cursor === null ? c : [...c, page.next_cursor]))}
              >
                Next
              </button>
            </div>
          </>
        ) : null}
      </div>
      {/* Mounted only while open, so each opening starts with an empty form and a fresh op_id. */}
      {dialog?.kind === "decide" && (
        <DecisionModal
          key={`${dialog.action}:${dialog.agent.caller_key}`}
          action={dialog.action}
          agent={dialog.agent}
          onClose={() => setDialog(null)}
          onRejected={async () => {
            setDialog(null);
            await refresh("Caller rejected. It is hidden from the New list and still counted under Rejected.");
          }}
          onRegistered={async (answer) => {
            setDialog({ kind: "invitation", agent: dialog.agent, answer });
            await queryClient.invalidateQueries({ queryKey: DISCOVERED_QUERY_ROOT });
          }}
        />
      )}
      {dialog?.kind === "invitation" && (
        <InvitationModal
          agent={dialog.agent}
          answer={dialog.answer}
          onClose={() => {
            setDialog(null);
            setNotice("Invitation issued. Give it to the Identity MCP of the agent you expect.");
          }}
        />
      )}
    </ScreenScaffold>
  );
}

function Row(props: {
  readonly agent: DiscoveredAgent;
  readonly isAdmin: boolean;
  readonly onAct: (action: "register" | "reject", agent: DiscoveredAgent, trigger: HTMLElement) => void;
}): ReactNode {
  const { agent, isAdmin } = props;
  const reasonId = useId();
  const decided = agent.status !== "new";
  return (
    <tr data-status={agent.status}>
      <th scope="row" className="disc__caller">
        <code title={agent.caller_key}>{shortKey(agent.caller_key)}</code>
        {decided && agent.decision_reason ? <span className="disc__why">{agent.decision_reason}</span> : null}
      </th>
      <td>
        <time dateTime={isoAttr(agent.first_seen)}>{formatDateTime(agent.first_seen)}</time>
      </td>
      <td>
        <time dateTime={isoAttr(agent.last_seen)}>{formatDateTime(agent.last_seen)}</time>
      </td>
      <td className="disc__num">{agent.call_count}</td>
      <td>{client(agent)}</td>
      <td>{agent.transport ?? "—"}</td>
      <td>{agent.gateways.length === 0 ? "—" : agent.gateways.map((g) => g.gateway_id).join(", ")}</td>
      <td>{topTools(agent)}</td>
      <td>
        {agent.status === "rejected" ? (
          <span className="disc__muted">Rejected</span>
        ) : (
          <span className="disc__actions">
            <button
              type="button"
              className="disc__btn"
              disabled={!isAdmin}
              aria-describedby={isAdmin ? undefined : reasonId}
              onClick={(e) => props.onAct("register", agent, e.currentTarget)}
            >
              {agent.status === "registered" ? "Issue new invitation" : "Register"}
            </button>
            <button
              type="button"
              className="disc__btn disc__btn--danger"
              disabled={!isAdmin}
              aria-describedby={isAdmin ? undefined : reasonId}
              onClick={(e) => props.onAct("reject", agent, e.currentTarget)}
            >
              Reject
            </button>
            {!isAdmin && (
              <span id={reasonId} className="disc__muted">
                Administrators only
              </span>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}

function DecisionModal(props: {
  readonly action: "register" | "reject";
  readonly agent: DiscoveredAgent;
  readonly onClose: () => void;
  readonly onRejected: () => Promise<void>;
  readonly onRegistered: (answer: RegisterAnswer["invitation"]) => Promise<void>;
}): ReactNode {
  const { action, agent } = props;
  const formId = useId();
  const reasonId = useId();
  const codeId = useId();
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One operation id per opened dialog: a retry of the same submission returns the same invitation.
  const opId = useMemo(() => newOpId(), []);
  const register = action === "register";

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || reason.trim().length === 0 || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const result = await sendDecision<Partial<RegisterAnswer>>(agent.caller_key, action, { reason: reason.trim(), op_id: opId, mfa_code: code });
      if (!result.ok) {
        // A missing or wrong code re-prompts in place: the reason stays, the code field is cleared.
        if (result.code === "mfa_required") setCode("");
        setError(decisionMessage(result.code));
        return;
      }
      if (!register) {
        await props.onRejected();
        return;
      }
      const invitation = result.body.invitation;
      if (invitation === undefined) {
        setError(decisionMessage("register_not_recorded"));
        return;
      }
      await props.onRegistered(invitation);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? () => undefined : props.onClose}
      role={register ? "dialog" : "alertdialog"}
      title={register ? "Register caller" : "Reject caller"}
      description={
        register
          ? `Issue a single-use enrollment invitation for ${shortKey(agent.caller_key)}. It expires within 24 hours and does not replace the approval step.`
          : `Reject ${shortKey(agent.caller_key)}? It is marked known and refused and hidden from the New list. This cannot be undone here.`
      }
      footer={
        <>
          <button type="button" className="disc__btn" onClick={props.onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            className={register ? "disc__btn" : "disc__btn disc__btn--danger"}
            disabled={busy || reason.trim().length === 0 || code.length !== 6}
          >
            {busy ? (register ? "Issuing…" : "Rejecting…") : register ? "Register and issue invitation" : "Reject caller"}
          </button>
        </>
      }
    >
      <form id={formId} className="disc__form" onSubmit={(e) => void submit(e)} noValidate>
        <div className="disc__field">
          <label htmlFor={reasonId}>Reason (required, recorded in the audit trail)</label>
          <textarea id={reasonId} value={reason} maxLength={256} rows={3} required onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="disc__field">
          <label htmlFor={codeId}>Two-factor code</label>
          <OtpInput id={codeId} value={code} onChange={setCode} disabled={busy} required align="start" />
        </div>
        {error !== null && (
          <p className="disc__err" role="alert">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

/** The signed invitation, shown once. It lives in this component's props only; closing the dialog drops it. */
function InvitationModal(props: {
  readonly agent: DiscoveredAgent;
  readonly answer: RegisterAnswer["invitation"];
  readonly onClose: () => void;
}): ReactNode {
  const valueId = useId();
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(props.answer.value);
      setCopied("copied");
    } catch {
      setCopied("failed");
    }
  };
  return (
    <Modal
      open
      onClose={props.onClose}
      title="Invitation issued"
      description={`For ${shortKey(props.agent.caller_key)}. This is the only time it is shown. Copy it now.`}
      footer={
        <button type="button" className="disc__btn" onClick={props.onClose}>
          Done
        </button>
      }
    >
      <div className="disc__form">
        <div className="disc__field">
          <label htmlFor={valueId}>Enrollment invitation</label>
          <RevealInput id={valueId} value={props.answer.value} onChange={() => undefined} ariaLabel="Enrollment invitation" autoComplete="off" />
        </div>
        <p>
          Expires <time dateTime={isoAttr(props.answer.expires_at)}>{formatDateTime(props.answer.expires_at)}</time>. It can be used once, by the first
          agent key that reaches enrollment, together with your licence key. The agent is still approved by a person.
        </p>
        <div className="disc__actions">
          <button type="button" className="disc__btn" onClick={() => void copy()}>
            Copy invitation
          </button>
          <span role="status" className="disc__muted">
            {copied === "copied" ? "Copied." : copied === "failed" ? "Could not copy. Select the text and copy it." : ""}
          </span>
        </div>
      </div>
    </Modal>
  );
}
